import { useState, useEffect } from "react";
import { classNames } from "primereact/utils";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import {
  CreateMessageDto,
  ForumMessageDTO,
  ForumTopicEnum,
  ForumWithMessagesDTO,
  getColorForLetter,
  ToggleVisibilityDto
} from "../../services/forum/forum.dto";
import forumService from "../../services/forum/forum.service";
import FormikValueIsValid from "../../utils/FormikValueIsValid";
import { useToast } from "../../utils/toast";
import { DataView } from "primereact/dataview";
import { useNavigate, useParams } from "react-router-dom";
import { Role } from "../../services/auth/auth.dto";
import { useAuth } from "../../utils/auth";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { BreadCrumb } from "primereact/breadcrumb";
import { MenuItem } from "primereact/menuitem";
import { cardData } from "./ForumHomePage";

interface Mesaj {
  mesaj: string;
  mesaj_kimden: string;
}

interface Topic {
  forum_header: string;
  message_content: string;
  forum_topic: ForumTopicEnum;
  mesajlar: Mesaj[];
  creatorName: string;
  creation_date: string;
  view_count: number;
  replies: number;
}

const ForumAyrinti = () => {
  const { idForum, topicId } = useParams<{ idForum: string; topicId: string }>();
  const topicIdNumber = Number(topicId);

  const [forum, setForum] = useState<ForumWithMessagesDTO | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useAuth();
  const items: MenuItem[] = [
    {
      label: "Forum",
      command: () => {
        navigate("/app/forum-homepage");
      }
    },
    {
      label: cardData[topicIdNumber - 1].title,
      command: () => {
        const value = cardData[topicIdNumber - 1].value; // cardData'dan valuee değerini al
        navigate(`/app/forum-page/${value}`);
      }
    },
    {
      label: forum?.forum_header
    }
  ];
  const home: MenuItem = {
    icon: "pi pi-home",
    command: () => {
      navigate("/app/forum-homepage");
    }
  };
  useEffect(() => {
    if (!idForum) {
      navigate("/forum");
      return;
    }
    forumService
      .GetForumById(Number(idForum), {
        blockUI: true
      })
      .then((res) => {
        if (res?.result?.error === null) {
          setForum(res?.result?.data);
        } else {
          toast.show(res?.result?.error || "Hata oluştu.", "error");
        }
      });
  }, []);

  const formik = useFormik<CreateMessageDto>({
    initialValues: {
      forum_id: idForum ? Number(idForum) : 0,
      message_content: ""
    },
    validate: (values) => {
      const errors: any = {};

      if (values.message_content.length < 10) {
        errors.message_content = "Lütfen en az 10 karakter içeren bir yorum yazınız.";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const res = await forumService.CreateNewMessage(values);
        if (res?.result?.error === null) {
          toast.show("Forum gönderiniz başarıyla oluşturuldu.", "success");
          // Sayfayı yenileme
          window.location.reload();
        } else {
          toast.show("Bir hata oluştu, lütfen tekrar deneyiniz.", "error");
        }
      } catch (error) {
        toast.show("Bir hata oluştu, lütfen tekrar deneyiniz.", "error");
      }
    }
  });

  const messageTemplate = (message: ForumMessageDTO, index: number) => {
    const firstLetter = message.creator_name.charAt(0).toUpperCase();
    const bgColor = getColorForLetter(firstLetter);

    const handleDelete = async (e: React.MouseEvent) => {
      try {
        confirmPopup({
          target: e.target as HTMLElement,
          message: "Silme işlemi geri alınamaz. Bu konuyu silmek istediğinize emin misiniz?",
          icon: "pi pi-info-circle",
          defaultFocus: "accept",
          acceptClassName: "p-button-success",
          acceptLabel: "Onayla",
          rejectLabel: "İptal",
          accept: async () => {
            const toggleVisibilityDto: ToggleVisibilityDto = { visibility: false };
            await forumService.ToggleMessageVisibility(message.id, toggleVisibilityDto);
          },
          reject: () => {}
        });

        //refetchForumPage(); // Refresh the list after deletion
      } catch (error) {
        toast.show("Konu silinirken bir hata oluştu", "error");
      }
    };

    return (
      <div
        className={classNames("col-12 flex flex-row p-2 gap-3 border-round", {
          "border-top-1 surface-border": index !== 0
        })}
        key={index}
      >
        <div
          className="w-3rem h-3rem flex justify-content-center align-items-center text-white text-xl font-bold border-circle shadow-2 my-1"
          style={{ backgroundColor: bgColor }}
        >
          <div className="flex align-items-center justify-content-center" style={{ height: "100%" }}>
            {firstLetter}
          </div>
        </div>

        <div className="flex flex-column flex-1">
          <div className="flex align-items-center gap-2 text-sm text-600">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-user text-primary-500"></i>
              <span className="font-medium text-secondary-900">{message.creator_name}</span>
            </div>
            <div className="flex align-items-center gap-2 ml-3">
              <i className="pi pi-calendar text-blue-500"></i>
              <span>
                {new Date(message.created_at).toLocaleDateString()} -
                {new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </div>
          </div>
          <div className="text-md text-900 mt-2">{message.message_content}</div>
        </div>

        {auth.getUser().role === Role.ADMIN && (
          <Button
            icon="pi pi-trash"
            severity="danger"
            text
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(e);
            }}
            className="p-button-rounded"
            tooltip="Sil"
          />
        )}
      </div>
    );
  };

  if (!forum) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <div className="">
      <BreadCrumb className="mb-0 lg:mb-3 mx-0 lg:mx-2" model={items} home={home} />
      <ConfirmPopup />
      <div className="card p-3 sm:p-4 mb-3">
        <div className="mb-2">
          <h2 className="text-xl sm:text-2xl font-bold text-primary-700 m-0">{forum.forum_header}</h2>
        </div>

        <p className="text-sm sm:text-base text-700 my-3 line-height-3">{forum.forum_content}</p>

        <div className="flex flex-wrap justify-content-between align-items-center text-sm text-600 pt-2 border-top-1 surface-border gap-2">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user text-primary-500"></i>
            <span className="font-medium text-secondary-900">{forum.creator_name}</span>
          </div>

          <div className="flex align-items-center gap-2">
            <i className="pi pi-calendar text-blue-500"></i>
            <span>
              {new Date(forum.creation_date).toLocaleDateString()} -
              {new Date(forum.creation_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>
      </div>

      <div className="card p-3 mb-3">
        <DataView
          value={forum.messages}
          itemTemplate={messageTemplate as any}
          layout="list"
          emptyMessage="Henüz hiç yorum yapılmamış."
        />
      </div>

      <div className="card p-3 sm:p-4 mb-3">
        <h3 className="text-lg sm:text-xl font-bold mb-3 text-primary-700">Yeni Mesaj Gönder</h3>
        <form onSubmit={formik.handleSubmit} className="flex flex-column gap-3">
          <div>
            <label htmlFor="message_content" className="block text-sm font-medium text-700 mb-2">
              Mesajınız
            </label>
            <InputTextarea
              id="message_content"
              name="message_content"
              value={formik.values.message_content}
              onChange={(e) => formik.setFieldValue("message_content", e.target.value)}
              rows={4}
              style={{ minHeight: "120px" }}
              placeholder="Mesajınızı buraya yazın..."
              className={classNames("w-full", { "p-invalid": FormikValueIsValid(formik, "message_content") })}
            />
            {FormikValueIsValid(formik, "message_content") && (
              <small className="p-error block mt-1">{formik.errors.message_content}</small>
            )}
          </div>

          <div className="flex justify-content-end">
            <Button
              label="Gönder"
              type="submit"
              icon="pi pi-send"
              iconPos="right"
              className="px-4"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForumAyrinti;
